/**
 * Legal Center content. As of 2026-06-21 the counsel-owned bracketed
 * placeholders are resolved (governing law + venue clause set to
 * Williamson County, TX; arbitration clause intentionally omitted;
 * vendor list reconciled with ``SECURITY_PROGRAM.md`` §8).
 *
 * Backs launch-compliance R6.6, R6.7, R6.8. The vendor table in the
 * Privacy doc MUST match the ``SECURITY_PROGRAM.md`` §8 list — change
 * one, change the other in the same PR. The test in
 * ``backend/tests/test_property_no_data_dumps_tracked.py`` is being
 * extended in launch-compliance Task 20.4 to enforce this.
 *
 * Updated: 2026-07-21 — Privacy vendor list reconciled with ``SECURITY_PROGRAM.md``
 * §8 after the Deepgram STT restoration (ZDR signed 2026-07-15): Deepgram is now
 * the primary streaming STT, AssemblyAI the backup. Keep the two lists in sync.
 *
 * Updated: 2026-09-15 — the Privacy "what we collect" list names the weekly parent
 * recap email (weekly-parent-digest). This is a user-visible change to the text, so
 * ``_TERMS_VERSION`` in ``backend/app/routes/public_config.py`` went 1 → 2 and every
 * tenant re-accepts once. A change to these documents that a family would notice
 * MUST carry that bump plus a ``terms_release_notes.json`` entry in the same PR.
 *
 * Updated: 2026-10-27 — the kidSAFE seal's place in the app moves from Parental Controls
 * to Settings > Legal, shown to a signed-in grown-up only (operator, 2026-10-27); no
 * version bump: it names where a disclosure happens, it changes nothing a family does.
 * Updated: 2026-10-27 — Sentry's line names errors reported from the app in the browser
 * (browser error monitoring, frontend/src/boot/errorReporting.ts; Lands, 2026-10-27); no
 * version bump: it names a disclosure, it changes nothing a family does.
 * Updated: 2026-10-24 — the kidSAFE seal: the pages that show it and that kidsafe.com
 * receives the visitor's IP address and the page address (operator and Lands, 2026-10-24);
 * no version bump: it names a disclosure, it changes nothing a family does.
 * Updated: 2026-10-23 — project entries (what a child types or taps into a project,
 * their own or a shared one) named under what we collect, the direct notice and
 * retention, ahead of the App kind (counsel, 2026-10-23); no version bump: it describes
 * a practice that does not exist yet and changes nothing a family does today.
 * Updated: 2026-10-21 — Replicate's one-hour deletion, autosprite's inputs, Plausible
 * named as the cookieless analytics on the journal and share pages, and a "Changes to
 * this policy" section (a material change is emailed to the primary account holder).
 */

export interface LegalSection {
  h: string;
  p?: string[];
  ul?: string[];
}

export interface LegalDoc {
  id: "privacy" | "safety" | "terms" | "support";
  label: string;
  icon: "lock" | "shield" | "book-open" | "life-buoy";
  updated: string;
  lede: string;
  sections: LegalSection[];
}

/** Keep in sync with ``_TERMS_VERSION`` in ``backend/app/routes/public_config.py``
 *  — that is the value actually served to the client at /config/public. */
export const TERMS_VERSION = 3;

export const LEGAL_DOCS: LegalDoc[] = [
  {
    id: "privacy",
    label: "Privacy Policy",
    icon: "lock",
    updated: "10/27/2026",
    lede:
      "world by me is a creative app where people build projects by talking with an AI helper. Because many children under 13 find the product valuable, we follow the Children's Online Privacy Protection Act (COPPA). This explains what we collect from children, why, who we share it with, how long we keep it, and your choices as a parent.",
    sections: [
      {
        h: "A parent is always in control",
        p: [
          "A child's account is created and overseen by a parent or guardian. Before we collect personal information to run the voice experience, we obtain a parent's verifiable consent. A parent can review the child's information, delete it, and withdraw consent at any time.",
        ],
      },
      {
        h: "What we collect, and how",
        ul: [
          "Account info you provide: a username, birthdate, a passphrase, and interests used to personalize the experience.",
          "Your voice, or the voice of anyone on your account, and what they say — carried to our processors, converted to text, and used to generate the helper's response. We do not store the raw audio recording.",
          "Conversation transcripts — the text — kept up to 12 months for problem diagnosis and parent review; never used to train AI models.",
          "Creations made on your account (projects, images, avatar).",
          "Entries a child makes while using a project on world by me, including a project another child shared, such as list items, counter values and quiz answers. These are saved under the child's own account for that project, so they can come back to it, and are never shown to other users, including the project's author. A verified parent can review and delete them.",
          "Gentle, derived 'growth' signals about engagement, used only for the parent-facing view — not a clinical assessment.",
          "A weekly recap email to you, with AI-written summaries of what each child on your account made and explored, based on the information above. It follows each child's privacy setting, and you can turn it off for any child, or entirely, in settings.",
          "Technical info needed to run and secure the Service.",
          "Parent info: email, billing (via our processor), and, if you choose to give it, a mailing address. We never ask a child for an address.",
        ],
        p: [
          "We don't condition participation on collecting more than is reasonably necessary, and we use no third-party advertising or analytics tracking in the children's experience. (Our journal, and pages that show a creation shared from an account where an adult has allowed sharing, count visits with Plausible, a cookieless analytics service, as described below.)",
        ],
      },
      {
        h: "Demo and unauthenticated visits",
        p: [
          "If a visitor uses our demo before creating an account, we collect no personal information. Audio is processed transiently and is not stored; transcripts and the AI's spoken responses are redacted before any retained record is written. We keep only non-identifying aggregate frequency for service planning.",
        ],
      },
      {
        h: "Who we share information with",
        p: [
          "Only service providers who help us run the Service, who may use it solely to perform services for us — never for their own purposes or advertising. We have data processing agreements in place with the providers that receive personal information about you or anyone on your account. Where a provider offers it, we have turned on zero data retention or opted out of its use for model training, as noted for that provider below; otherwise the provider's standard retention applies.",
          "Providers include: AWS (hosting and storage, including our web app; text-to-speech via Polly, which receives only the assistant's words, never anyone's voice), LiveKit (real-time voice transport — recording and screenshot egress disabled), Deepgram (streaming speech-to-text, under Zero Data Retention), AssemblyAI (a standby speech-to-text service we can switch to if Deepgram is unavailable, under Model-Improvement opt-out), Replicate (avatar, pet and creation image generation; it receives a description our AI writes, with anything we identify as personal details removed before the prompt is used to generate an image; Replicate deletes the prompt, the picture and its logs an hour after each request), autosprite.io (animates a generated avatar picture; it receives only that picture and a random name, never anything that identifies you or anyone on your account), didit.me (verifying a parent or guardian's identity for parental consent), Stripe (billing on the web), Apple (purchases in the iPhone and iPad app go through the App Store, and each purchase carries an identifier tied to your account so we can apply it; once we turn them on, Apple also delivers push notifications to your device), SendGrid (email to you), Sentry (error monitoring, with PII scrubbing, including errors reported from the app in your browser), and Plausible (cookieless analytics: visit counts on our journal and on pages that show a creation shared from an account where an adult has allowed sharing; it never runs inside the app, sets no cookies and collects no personal information). AI requests are handled by OpenAI (the AI that builds creations, image generation via gpt-image-1, and image moderation, under Zero Data Retention) or by OpenRouter (a routing service that passes the request to a model provider; it deletes each request and reply promptly after the reply is generated, because we do not turn on its logging, and we route only to providers listed as not retaining or training on your data). You may consent to our collection and use without consenting to any disclosure that isn't integral to the Service.",
          "We show our kidSAFE Seal Program membership seal on our public pages (our home page on a device we don't recognize, our journal, this Privacy Policy and our Terms) and in the app under Settings, then Legal, when a grown-up is signed in. The seal loads from kidsafe.com, which receives the visitor's IP address and the address of the page; it sets no cookies. It is never shown on the sign-in screen or to a child.",
        ],
      },
      {
        h: "How long we keep it",
        ul: [
          "Account info: while active; deleted within 30 days after account deletion.",
          "Conversation transcripts: 12 months rolling, then auto-deleted. Not used to train AI models.",
          "Voice session metadata: 90 days.",
          "Observability events: 90 days; content is suppressed.",
          "Growth signals: while the account is active; deleted on account deletion.",
          "Project entries (what a child types or taps into a project): while the project is used; deleted 12 months after the child last used that project, or on account deletion.",
          "Mailing address: so we can occasionally send subscribers free things, which might be something like stickers of your child's avatar. We never promise to send anything and never charge for anything sent this way. We share the address only with the postal service or courier that delivers it, and we keep it while your account is active so we can send future items. It is deleted with your account.",
          "Parent verification info: status only, retained for the life of the child account plus one year; raw identity data is not retained.",
        ],
        p: [
          "Safety carve-out: if needed to investigate a safety concern, a report of harmful conduct, or a rules violation, we may retain the specific related records (including relevant messages and AI responses) for as long as necessary to address it and meet legal obligations, even after an account is deleted.",
        ],
      },
      {
        h: "Your choices as a parent",
        p: [
          "At any time you can review, delete, or export your child's information, or withdraw consent (which stops the voice experience). Use the parent dashboard or contact us — we may verify your identity first.",
        ],
      },
      {
        h: "Security",
        p: [
          "We maintain a written information security program and reasonable administrative, technical, and physical safeguards, including encryption in transit and at rest and access controls limiting who can view children's information.",
        ],
      },
      {
        h: "Changes to this policy",
        p: [
          "If we change how we collect, use or share information about you or anyone on your account in a way that matters, we will email you before the change takes effect, and ask for permission again where appropriate. Smaller changes, such as clearer wording, are shown by the \"Last updated\" date at the top.",
        ],
      },
    ],
  },
  {
    id: "safety",
    label: "Safety (COPPA)",
    icon: "shield",
    updated: "6/15/2026",
    lede:
      "Before a child can use world by me, a parent or guardian gives permission. Here's the short version of what we'd collect and the choices you have — the kind of direct notice we show in the consent flow.",
    sections: [
      {
        h: "Why you're seeing this",
        p: [
          "You started setting up an account for your child, or your child is using world by me without you having said okay yet. We need a parent or guardian to agree before friends features turn on and before we keep certain kinds of information.",
        ],
      },
      {
        h: "What we collect from a child",
        p: [
          "We collect a username, birthdate, passphrase, interests, the voice the child uses while creating, the AI's responses, the creations the child makes, and entries the child makes while using a project (their own or one another child shared), saved under the child's own account for that project and never shown to other users. We do not store raw audio; transcripts age out on a 12-month schedule and are not used to train AI models.",
        ],
      },
      {
        h: "What we won't do",
        p: [
          "We will not use children's information for advertising. We will not sell it. We will not let our service providers use it for their own purposes or to train their own models.",
        ],
      },
    ],
  },
  {
    id: "terms",
    label: "Terms of Service",
    icon: "book-open",
    updated: "10/23/2026",
    lede:
      "Effective October 23, 2026. These Terms are a legal agreement between world by me Inc. and the person who creates, manages, pays for, or uses an account or profile on the Service. By creating an account, starting a subscription, using the Service, approving a child profile, or continuing to use the Service after we post an updated version, you agree to these Terms.",
    sections: [
      {
        h: "1. Welcome",
        p: [
          "Welcome to world by me. We provide a voice-first creative platform where people can build, explore, play, animate, tell stories, and create interactive projects with help from creative tools, including AI-assisted tools (the Service).",
          "These Terms of Service (the Terms) are a legal agreement between world by me Inc. (world by me, we, us, or our) and the person who creates, manages, pays for, or uses an account or profile on the Service (you). These Terms apply to our website, mobile apps, web app, creator tools, child profiles, parent controls, subscriptions, free modes, support services, and any other feature that links to these Terms.",
          "Please read these Terms carefully. By creating an account, starting a subscription, using the Service, approving a child profile, or continuing to use the Service after we post an updated version, you agree to these Terms.",
        ],
      },
      {
        h: "2.1 Adults, parents, teens, and children",
        p: [
          "world by me is a general-audience creative product that is also designed to be safe and useful for children as well as adults. Different account and privacy rules apply depending on a user's age and whether a parent or guardian manages the account.",
        ],
      },
      {
        h: "2.2 Parents and guardians are the contracting party for child profiles",
        p: [
          "If you create, approve, manage, or pay for a child profile, you represent that you are the child's parent or legal guardian, or that you have authority to act for that child. You are responsible for the child's use of the Service and for making sure the child follows these Terms.",
          "Children under 13 may not independently agree to these Terms. A parent or guardian must approve the child's use of features that require parental consent. If a child under 13 begins in a limited trial or cabined experience, the child may not unlock persistent or expanded features until a parent or guardian completes the required consent process.",
        ],
      },
      {
        h: "2.3 Teen users",
        p: [
          "Users who are 13 to 17 may use the Service subject to these Terms. If a teen account is connected to a parent or guardian account, the parent or guardian may have oversight, safety, privacy, or account-management controls as described in the Service and our Privacy Policy.",
        ],
      },
      {
        h: "2.4 Age information must be accurate",
        p: [
          "You agree to provide accurate age, birthdate, account, billing, and consent information. Do not misrepresent a child's age or identity to bypass age-appropriate safety settings.",
        ],
      },
      {
        h: "3.1 Account security",
        p: [
          "You are responsible for keeping your login credentials safe and for activity that occurs through your account, except where caused by our failure to use reasonable security measures. Tell us promptly if you believe your account or a child profile has been accessed without permission.",
        ],
      },
      {
        h: "3.2 Child profiles",
        p: [
          "A child profile may include a username, birthdate or age band, avatar, interests, creations, projects, conversation history, parent controls, and settings. Parents and guardians can manage child profiles through the parent controls available in the Service.",
        ],
      },
      {
        h: "3.3 Consent, withdrawal, export, and deletion",
        p: [
          "For children under 13, we obtain verifiable parental consent before collecting, using, or disclosing personal information where required by law. Parents and guardians may use in-app controls to review certain child information, request export, withdraw consent, or request deletion. When consent is withdrawn for a child profile, features that require the relevant information may stop working. Deletion may include a recoverable grace period described in the Service and Privacy Policy.",
        ],
      },
      {
        h: "3.4 Re-acceptance of updated policies",
        p: [
          "If we make material changes to these Terms or to our Privacy Policy, we may require a parent, guardian, adult user, or teen user to review and accept the updated terms before continuing to use certain features.",
        ],
      },
      {
        h: "4.1 Free trial with a payment method",
        p: [
          "We may offer a free trial for a paid plan. Unless the purchase screen says otherwise, the current trial length is 7 days. If you start a trial with a valid payment method, the subscription will automatically convert to a paid subscription at the end of the trial unless you cancel before the trial ends.",
        ],
      },
      {
        h: "4.2 No-card or no-consent trial",
        p: [
          "We may offer a limited experience that lets a child or other user try creation features before a paid subscription or completed parental-consent process. This mode is intended to be temporary and limited. It may not include persistent account features, full personalization, sharing, child-to-child communication, or paid creator features.",
          "No personal information is collected from a child under 13 years of age without parental consent during a trial period beyond what is necessary for the limited trial, security, abuse prevention, or transfer into a parent-approved account. If a parent later approves an account, we may allow the user to carry over creations from the same temporary session or device.",
        ],
      },
      {
        h: "4.3 Explore mode",
        p: [
          "We may provide a free Explore mode. Explore mode may let users browse, play, or view shared, curated, or sample worlds, but may limit or disable features such as creating, saving, avatars, voice creation, social features, exports, or parent reporting. Explore mode is provided at our discretion and may change over time.",
        ],
      },
      {
        h: "5.1 Plans",
        p: ["The current launch plans are:"],
        ul: [
          "Single Plan: $14.99/month for 1 creator profile.",
          "Family Plan: $24.99/month for up to 3 child creator profiles.",
        ],
      },
      {
        h: "5.1 Plans (continued)",
        p: [
          "Plan names, prices, features, and usage limits may change over time. We will disclose the price and renewal period before you subscribe.",
        ],
      },
      {
        h: "5.2 No promise of limitless use",
        p: [
          "The Service is not offered on a limitless or unmetered basis. We may apply reasonable usage limits, technical limits, safety limits, rate limits, storage limits, voice-session limits, generation limits, or anti-abuse controls. We will try to design these limits so normal family use works well, but we may limit, slow, or suspend usage that creates cost, safety, security, or reliability problems.",
        ],
      },
      {
        h: "5.3 Billing by purchase channel",
        p: [
          "Where you subscribe determines who bills you and how cancellation and refunds work:",
        ],
        ul: [
          "Web subscriptions are processed through our payment processor, currently expected to be Stripe. We do not store your full card number.",
          "iOS subscriptions are billed by Apple through Apple in-app purchase.",
          "Android subscriptions are billed by Google through Google Play Billing.",
        ],
      },
      {
        h: "5.3 Billing by purchase channel (continued)",
        p: [
          "If you purchased through Apple or Google, your subscription, cancellation, renewal, and refund options are controlled by that app store's terms and account-management tools. We may not be able to cancel or refund an app-store purchase directly.",
        ],
      },
      {
        h: "5.4 Auto-renewal",
        p: [
          "Paid subscriptions renew automatically each billing period until canceled, unless the purchase screen says otherwise. By starting a subscription, you authorize the applicable billing provider to charge the payment method on file for recurring fees, taxes, and any applicable charges.",
        ],
      },
      {
        h: "5.5 Cancellation",
        p: [
          "You may cancel a subscription at any time. Cancellation stops the next renewal; it does not automatically refund the current billing period unless required by law, app-store policy, or our stated refund policy.",
        ],
        ul: [
          "For web subscriptions, cancel through account billing settings or by contacting support if account settings are unavailable.",
          "For Apple subscriptions, cancel through your Apple account subscription settings.",
          "For Google Play subscriptions, cancel through Google Play subscription settings. Uninstalling the app does not cancel a Google Play subscription.",
        ],
      },
      {
        h: "5.5 Cancellation (continued)",
        p: [
          "After cancellation, you may continue to have access to paid features until the end of the current paid period unless the billing provider or applicable law provides otherwise.",
        ],
      },
      {
        h: "5.6 Refund policy",
        p: [
          "Counsel/business decision required before launch. Suggested default: For web subscriptions, subscription fees are non-refundable except where required by law or where we state otherwise in writing. We may, in our discretion, offer refunds or credits. App-store purchases are handled under Apple's or Google's refund rules. Nothing in these Terms limits any non-waivable consumer rights.",
        ],
      },
      {
        h: "5.7 Lapse, failed payment, and downgrade",
        p: [
          "If a subscription lapses, is canceled, or cannot be renewed, paid creator features may be paused or moved to Explore mode. We do not intend to delete creations solely because a subscription lapses, but access to editing, voice creation, exports, sharing, or other paid features may be limited until the subscription is restored.",
          "If a Family Plan is downgraded to a Single Plan, extra child creator profiles may be moved to Explore mode or otherwise limited. We do not intend to delete their creations solely because of the downgrade.",
        ],
      },
      {
        h: "5.8 Taxes and price changes",
        p: [
          "Prices do not include taxes unless stated otherwise. We may change prices or plan features with notice as required by law and the applicable billing platform.",
        ],
      },
      {
        h: "6.1 What the tools do",
        p: [
          "The Service may use AI-assisted tools to help users brainstorm, build, animate, generate images, create game logic, narrate stories, synthesize speech, or improve projects. The AI helper is a creative tool, not a human, friend, therapist, doctor, teacher, lawyer, financial adviser, or emergency service.",
        ],
      },
      {
        h: "6.2 AI outputs may be imperfect",
        p: [
          "AI-generated responses and outputs can be inaccurate, incomplete, repetitive, surprising, or inappropriate despite our safety measures. You should review AI outputs before relying on them, sharing them, printing them, or using them outside the Service.",
        ],
      },
      {
        h: "6.3 No clinical, developmental, educational, or diagnostic promise",
        p: [
          "world by me may provide parent-facing observations, activity summaries, creative progress notes, or growth signals based on in-app activity. These features are designed to support parent-child connection and creative reflection. They are not medical, psychological, developmental, educational, diagnostic, or clinical assessments. They should not be used to diagnose, treat, evaluate, rank, or make decisions about a child's health, development, education, disability status, or wellbeing.",
        ],
      },
      {
        h: "6.4 Voice, transcripts, and speech features",
        p: [
          "The Service may process voice input through audio transport, speech-to-text, language-model, and text-to-speech providers. Our Privacy Policy describes what is collected, retained, deleted, and shared with processors. Do not use voice features to submit sensitive information that is not needed for creative play.",
        ],
      },
      {
        h: "6.5 No emergency use",
        p: [
          "The Service is not designed to detect emergencies or provide emergency help. If a user is in danger or needs urgent help, contact local emergency services or a trusted adult immediately.",
        ],
      },
      {
        h: "7.1 Your creations remain yours",
        p: [
          "As between you and world by me, you or your family keep ownership of the projects, prompts, stories, drawings, avatars, game ideas, animations, code-like instructions, images, and other content you create or submit through the Service (User Content), subject to rights owned by others and the licenses in these Terms.",
        ],
      },
      {
        h: "7.2 License to operate the Service",
        p: [
          "You grant world by me a worldwide, non-exclusive, royalty-free license to host, store, copy, process, transmit, display, perform, modify, adapt, create technical derivatives of, and otherwise use User Content as needed to provide, secure, improve, support, moderate, and operate the Service. This license includes use for thumbnails, previews, parent dashboards, safety review, debugging, backup, export, and account recovery.",
        ],
      },
      {
        h: "7.3 License for sharing and future social features",
        p: [
          "At launch, social sharing may be disabled or limited. If you choose to share, publish, invite, co-create, remix, or make content visible to another user in the future, you grant world by me the additional rights needed to display that content to the people you selected or to the audience you chose, and you grant those users the ability to view, play, interact with, remix, or co-create with the content as permitted by the Service settings.",
          "For child users, sharing and co-creation may require parent approval, friend relationships, safety review, account settings, or other limits. We may remove, restrict, or refuse to display shared content at any time for safety, legal, or policy reasons.",
        ],
      },
      {
        h: "7.4 Co-created content",
        p: [
          "Future co-creation or multiplayer features may allow more than one user to contribute to a shared project, room, game, animation, or build queue. Unless a feature says otherwise, each contributor retains rights in what they contribute, and each contributor grants world by me and the other permitted participants the rights needed to continue using, editing, playing, saving, and displaying the shared creation within the Service. Parent-controlled permissions may limit a child's ability to join, invite, speak, hear peer audio, edit, or share.",
        ],
      },
      {
        h: "7.5 AI-generated content",
        p: [
          "To the extent permitted by applicable law and our provider terms, we assign to you any rights we may have in AI-generated output created for your account or child profile, excluding world by me software, templates, models, prompts, systems, brand assets, curated assets, and third-party content. We do not guarantee that AI output is unique or that another user will not receive similar output.",
        ],
      },
      {
        h: "7.6 Our content and software",
        p: [
          "The Service, software, templates, characters, interface, prompts, systems, models, curated assets, trademarks, logos, designs, documentation, and other materials we provide are owned by world by me or our licensors. We grant you a limited, personal, non-exclusive, non-transferable, revocable license to use the Service as allowed by these Terms.",
        ],
      },
      {
        h: "7.7 Content deletion",
        p: [
          "Deleting an account or child profile may delete or disable access to User Content as described in the Service and Privacy Policy. Some copies may remain temporarily in backups, logs, cached copies, safety records, app-store records, legal records, or content already shared with others where deletion is not technically or legally immediate. We will handle deletion requests as described in our Privacy Policy and applicable law.",
        ],
      },
      {
        h: "7.8 Feedback",
        p: [
          "If you send us ideas, suggestions, bug reports, or feedback, you allow us to use them without restriction or compensation, but we will not claim ownership of your underlying User Content merely because you gave feedback.",
        ],
      },
      {
        h: "8. Acceptable use",
        p: [
          "You agree not to misuse the Service or help anyone else misuse it. You may not:",
        ],
        ul: [
          "use the Service for unlawful, harmful, deceptive, exploitative, or abusive purposes;",
          "upload, create, request, or share sexual, violent, hateful, harassing, self-harm, extremist, exploitative, or age-inappropriate content;",
          "attempt to contact, groom, exploit, locate, identify, or manipulate a child or other user;",
          "share personal information about a child or another person without appropriate authority;",
          "impersonate another person or misrepresent your age, identity, authority, or relationship to a child;",
          "bypass age gates, parent gates, consent flows, privacy settings, friend approvals, usage limits, safety filters, or moderation systems;",
          "use the Service to generate malware, phishing, spam, scams, weapons instructions, or other dangerous content;",
          "interfere with, reverse engineer, scrape, overload, probe, or attack the Service or its providers;",
          "use automated systems to create accounts, harvest content, or abuse generation features;",
          "use the Service in a way that infringes intellectual property, privacy, publicity, or other rights;",
          "submit prompts or content intended to defeat child-safety protections or obtain inappropriate AI output;",
          "sell, rent, sublicense, or commercially exploit the Service except as we expressly allow.",
        ],
      },
      {
        h: "8. Acceptable use (continued)",
        p: [
          "We may block, remove, restrict, or report content or accounts that we believe violate these Terms, threaten safety, violate law, or create risk for world by me, users, children, families, or third parties.",
        ],
      },
      {
        h: "9. Safety, moderation, and reporting",
        p: [
          "We may use automated systems, human review, parent reporting tools, content filters, rate limits, account restrictions, and other safety measures to protect users. Safety tools are not perfect, and we do not guarantee that every inappropriate input or output will be blocked.",
          "Users and parents may be able to report creations, AI responses, messages, shared rooms, friend interactions, or other content. Parents may receive a triage queue or safety context depending on the feature and settings.",
          "If we believe content or conduct creates a risk of harm, violates law, or threatens child safety, we may preserve relevant information, notify a parent or guardian, suspend features, contact law enforcement, or take other protective action.",
        ],
      },
      {
        h: "10. Future and beta features",
        p: [
          "Some features may be experimental, limited, waitlisted, or feature-flagged, including co-creation, friend visits, shared rooms, public or parent-approved sharing, multiplayer games, animation tools, board games, pets, physical items, parent digests, growth-signal charts, and other verticals. We may add, remove, pause, or change these features at any time.",
          "Beta features may be less reliable and may have additional terms, consent flows, privacy notices, or safety requirements. A child's access to social, co-creation, public, directory, live voice, or multiplayer features may require parent approval and may be disabled by default.",
        ],
      },
      {
        h: "11. Physical goods and shipping",
        p: [
          "We may offer optional physical goods, such as stickers of a child's avatar or a small gift, as a trial, promotion, or paid product. Physical goods may require a shipping address and may be subject to additional terms, availability, fulfillment timing, substitutions, taxes, shipping fees, and geographic limits. Unless a purchase screen says otherwise, a statement that we may, would love to, or hope to send a physical item is not a guarantee.",
        ],
      },
      {
        h: "12. Third-party services and app stores",
        p: [
          "The Service may rely on third-party providers for hosting, voice transport, speech-to-text, language-model processing, text-to-speech, image generation, payments, parental verification, email, app-store billing, crash/error monitoring, and similar functions. Our Privacy Policy identifies the categories of providers and, where applicable, sub-processors.",
          "Third-party services may have their own terms and privacy policies. Your use of mobile apps may also be subject to Apple's App Store terms, Google Play terms, and device-platform rules. If these Terms conflict with mandatory app-store terms for an in-app purchase or mobile app, the app-store terms may control for that purchase or platform-specific issue.",
        ],
      },
      {
        h: "13. Copyright and intellectual property complaints",
        p: [
          "If you believe content on the Service infringes your copyright, contact us at legal@worldby.me with:",
        ],
        ul: [
          "your name and contact information;",
          "identification of the copyrighted work;",
          "identification of the allegedly infringing content and where it appears;",
          "a statement that you have a good-faith belief the use is not authorized;",
          "a statement, under penalty of perjury, that the information is accurate and you are authorized to act;",
          "your physical or electronic signature.",
        ],
      },
      {
        h: "13. Copyright complaints (continued)",
        p: [
          "We may remove content, disable accounts, or terminate repeat infringers in appropriate circumstances. Counsel should convert this section into a full DMCA policy and designate a DMCA agent if public UGC or sharing is enabled.",
        ],
      },
      {
        h: "14. Suspension and termination",
        p: [
          "You may stop using the Service at any time. We may suspend or terminate access, remove content, or limit features if we believe that:",
        ],
        ul: [
          "you or a child profile violated these Terms;",
          "continued access creates safety, legal, security, payment, or operational risk;",
          "we are required to do so by law, an app store, a provider, or a court order;",
          "your subscription is canceled, unpaid, or fraudulent;",
          "we discontinue a feature or the Service.",
        ],
      },
      {
        h: "14. Suspension and termination (continued)",
        p: [
          "After termination, sections that by their nature should survive will survive, including ownership, licenses needed for prior operation and legal compliance, payment obligations, disclaimers, limits of liability, indemnity, dispute terms, and general provisions.",
        ],
      },
      {
        h: "15. Changes to the Service or Terms",
        p: [
          "We may update the Service and these Terms from time to time. If changes are material, we will provide notice as required by law, such as in-app notice, email, or a re-acceptance screen. The updated Terms will apply after the effective date stated in the notice. If you do not agree, you must stop using the Service and cancel any subscription.",
        ],
      },
      {
        h: "16. Disclaimers",
        p: [
          "To the maximum extent permitted by law, the Service is provided as is and as available. We disclaim warranties of merchantability, fitness for a particular purpose, title, non-infringement, uninterrupted availability, error-free operation, accuracy, safety-filter perfection, or that content or AI output will meet your expectations.",
          "Some jurisdictions do not allow certain disclaimers, so some of the above may not apply to you. Nothing in these Terms limits rights that cannot be waived under applicable law.",
        ],
      },
      {
        h: "17. Limitation of liability",
        p: [
          "To the maximum extent permitted by law, world by me and its officers, directors, employees, agents, affiliates, providers, and licensors will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost data, lost goodwill, service interruption, or substitute services.",
          "To the maximum extent permitted by law, our total liability for all claims relating to the Service or these Terms will be limited to the greater of: (a) the amount you paid to world by me for the Service in the 3 months before the event giving rise to the claim; or (b) $100.",
          "This section does not limit liability that cannot be limited under applicable law, including liability for gross negligence, willful misconduct, or certain consumer-protection rights where non-waivable.",
        ],
      },
      {
        h: "18. Indemnity",
        p: [
          "If you are an adult user, parent, guardian, or entity using the Service, you agree to defend, indemnify, and hold harmless world by me and its officers, directors, employees, agents, affiliates, providers, and licensors from claims, damages, losses, liabilities, costs, and expenses, including reasonable attorneys' fees, arising from your User Content, your misuse of the Service, your violation of these Terms, your violation of law, or your violation of another person's rights.",
          "This indemnity does not apply to the extent a claim is caused by our own violation of these Terms or applicable law, and it does not apply to child users.",
        ],
      },
      {
        h: "19. Governing law and disputes",
        p: [
          "These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law rules, except where your local consumer law requires otherwise. The parties consent to the exclusive jurisdiction and venue of the state and federal courts with jurisdiction over Williamson County, Texas, for any dispute not resolved in small-claims court.",
        ],
      },
      {
        h: "20. Apple-specific terms",
        p: [
          "If you download or use our iOS app, Apple is not a party to these Terms and is not responsible for the Service or its content. Apple and its subsidiaries are third-party beneficiaries of this section and may enforce it against you to the extent required by Apple's terms. Your use of the iOS app must comply with Apple's App Store terms.",
          "Counsel should align this section with Apple's current standard end-user license agreement requirements or attach a custom EULA if needed.",
        ],
      },
      {
        h: "21. General terms",
        p: [
          "You may not assign these Terms without our consent. We may assign these Terms as part of a merger, acquisition, financing, reorganization, sale of assets, or by operation of law. If any provision is unenforceable, the remaining provisions will remain in effect. Our failure to enforce a provision is not a waiver. These Terms, together with the Privacy Policy, COPPA/Children's Privacy Notice, purchase terms, and any feature-specific terms, are the entire agreement between you and world by me for the Service.",
        ],
      },
      {
        h: "22. Contact",
        p: [
          "Questions about these Terms or the Service can be sent to: world by me Inc., 202 Walton Way Ste 192 Unit 744, Cedar Park, TX 78613. Email: legal@worldby.me.",
        ],
      },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: "life-buoy",
    updated: "6/15/2026",
    lede:
      "If anything in the app feels off — a creation went wrong, an AI response felt uncomfortable, billing looks unexpected — contact us and a person will read it.",
    sections: [
      {
        h: "Contact",
        p: [
          "Email: support@worldby.me.",
          "By post: world by me Inc., 202 Walton Way Ste 192 Unit 744, Cedar Park, TX 78613.",
        ],
      },
      {
        h: "Reporting a problem with a creation, user, or AI response",
        p: [
          "Open the project's overflow menu and tap 'Report content', or use the overflow on a friend's profile to report or block. Reports go to our triage queue; we read them.",
        ],
      },
      {
        h: "Parental data rights",
        p: [
          "Visit Parental Controls → Data review to see everything we have on file for your family, export it, or delete the whole family account.",
        ],
      },
    ],
  },
];

export type LegalDocId = LegalDoc["id"];
